import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Navigation, MapPin, Search, Loader2, Save, Eye, Check, Bell, BellRing,
  ArrowRight, Sparkles, Package, AlertCircle, Crosshair, RefreshCw,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatKm, SIZE_LABEL, URGENCY_LABEL } from "@/lib/format";
import AddressAutocompleteInput from "@/components/AddressAutocompleteInput";

const PRESET_DETOURS = [3, 5, 10];
const WEEKDAY_OPTIONS = [
  { id: "mon", label: "Lun" }, { id: "tue", label: "Mar" }, { id: "wed", label: "Mer" },
  { id: "thu", label: "Gio" }, { id: "fri", label: "Ven" }, { id: "sat", label: "Sab" },
  { id: "sun", label: "Dom" },
];

function compatColor(score) {
  if (score >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (score >= 60) return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-amber-50 text-amber-800 border-amber-200";
}

export default function DriverRouteSearch() {
  const navigate = useNavigate();
  const [startPos, setStartPos] = useState(null);
  const [gpsState, setGpsState] = useState("idle"); // idle|requesting|granted|denied|unavailable
  const [manualStart, setManualStart] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [endAddress, setEndAddress] = useState("");
  const [endPlace, setEndPlace] = useState(null); // {address, lat, lng} dopo selezione suggest
  const [maxDetourKm, setMaxDetourKm] = useState(5);
  const [customDetour, setCustomDetour] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [tempSaving, setTempSaving] = useState(false);
  const [tempActive, setTempActive] = useState(false);
  const [tempExpiresAt, setTempExpiresAt] = useState(null);
  const [isIOS] = useState(() =>
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
  );
  const mounted = useRef(true);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const requestGps = () => {
    if (!("geolocation" in navigator)) { setGpsState("unavailable"); return; }
    setGpsState("requesting");
    // Phase 43.26.3 — safety timeout: alcuni browser non chiamano né success né error.
    const safetyTimer = setTimeout(() => {
      if (mounted.current) {
        setGpsState((s) => (s === "requesting" ? "denied" : s));
      }
    }, 15000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(safetyTimer);
        if (!mounted.current) return;
        setStartPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsState("granted");
        setManualMode(false);
      },
      (err) => {
        clearTimeout(safetyTimer);
        if (!mounted.current) return;
        // err.code 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        setGpsState(err && err.code === 2 ? "unavailable" : "denied");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  // Phase 43.26.3 — iOS Safari richiede user-gesture per getCurrentPosition.
  // Quindi NON la chiamiamo automaticamente al mount. Auto-trigger SOLO se la
  // Permissions API conferma che il permesso è già `granted` (no prompt richiesto).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!("geolocation" in navigator)) { setGpsState("unavailable"); return; }
        if (!navigator.permissions || !navigator.permissions.query) {
          // iOS Safari < 16 non supporta Permissions API → mostra il banner CTA.
          setGpsState("idle");
          return;
        }
        const p = await navigator.permissions.query({ name: "geolocation" });
        if (cancelled) return;
        if (p.state === "granted") {
          requestGps();
        } else if (p.state === "denied") {
          setGpsState("denied");
        } else {
          setGpsState("idle"); // prompt: aspetta click utente
        }
      } catch {
        setGpsState("idle");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const search = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const endTrim = (endPlace?.address || endAddress).trim();
    if (!endTrim) { toast.error("Inserisci una destinazione"); return; }
    if (!endPlace || endPlace.lat == null || endPlace.lng == null) {
      toast.error("Seleziona una destinazione dai suggerimenti.");
      return;
    }
    if (!startPos && !(manualMode && manualStart.trim())) {
      toast.error("Abilita la posizione o inserisci una partenza manuale.");
      return;
    }
    setLoading(true);
    try {
      const body = {
        end_address: endTrim,
        end_lat: endPlace.lat,
        end_lng: endPlace.lng,
        max_detour_km: Number(customDetour) > 0 ? Number(customDetour) : maxDetourKm,
      };
      if (startPos) { body.start_lat = startPos.lat; body.start_lng = startPos.lng; }
      else if (manualStart.trim()) body.start_address = manualStart.trim();
      const { data } = await api.post("/driver/route-search", body);
      setData(data);
      if ((data.results || []).length === 0) {
        toast.info("Nessuna consegna disponibile sulla tua strada. Prova ad aumentare la deviazione massima.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const acceptOrder = async (orderId) => {
    setAcceptingId(orderId);
    try {
      await api.post(`/orders/${orderId}/accept`);
      toast.success("Ordine accettato");
      navigate(`/driver/orders/${orderId}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAcceptingId(null);
    }
  };

  const handleEndChange = useCallback((eOrText) => {
    const v = typeof eOrText === "string" ? eOrText : eOrText?.target?.value || "";
    setEndAddress((prev) => (prev === v ? prev : v));
    setEndPlace((prev) => (prev && v !== prev.address ? null : prev));
  }, []);

  const handleEndPlaceSelected = useCallback((p) => {
    if (!p || p.lat == null || p.lng == null) return;
    setEndPlace({ address: p.address, lat: p.lat, lng: p.lng });
    setEndAddress(p.address);
  }, []);


  const finalDetour = Number(customDetour) > 0 ? Number(customDetour) : maxDetourKm;
  const gpsReady = !!startPos;
  const endReady = !!(endPlace && endPlace.lat != null && endPlace.lng != null);
  const canSearch = (gpsReady || (manualMode && manualStart.trim().length > 0)) && endReady;

  // Phase 43.31 — "Avvisami" toggle: salva la ricerca come temporary route (TTL 2h).
  // Funziona SEMPRE: se non abbiamo lat/lng nel client, geocodifichiamo al volo
  // via /api/maps/geocode usando il testo inserito.
  const onSaveTemporary = useCallback(async () => {
    setTempSaving(true);
    try {
      if (tempActive) {
        await api.delete("/driver/route-search/clear-temporary");
        setTempActive(false);
        setTempExpiresAt(null);
        toast.success("Avvisi disattivati.");
        setTempSaving(false);
        return;
      }

      // Resolve START
      let start = null;
      if (data?.start) {
        start = { lat: data.start.lat, lng: data.start.lng, address: data.start.address || "" };
      } else if (startPos) {
        start = { lat: startPos.lat, lng: startPos.lng, address: "Posizione corrente" };
      } else if (manualMode && manualStart.trim()) {
        const { data: g } = await api.post("/maps/geocode", { address: manualStart.trim() });
        if (g?.found) {
          start = { lat: g.lat, lng: g.lng, address: g.formatted_address || manualStart };
        } else {
          toast.error(`Non riesco a trovare "${manualStart}". Prova un indirizzo più preciso.`);
          setTempSaving(false);
          return;
        }
      } else {
        toast.error("Inserisci una partenza (GPS o manuale).");
        setTempSaving(false);
        return;
      }

      // Resolve END
      let end = null;
      if (data?.end) {
        end = { lat: data.end.lat, lng: data.end.lng, address: data.end.address || "" };
      } else if (endPlace && endPlace.lat != null && endPlace.lng != null) {
        end = { lat: endPlace.lat, lng: endPlace.lng, address: endPlace.address || "" };
      } else if (endAddress.trim()) {
        const { data: g } = await api.post("/maps/geocode", { address: endAddress.trim() });
        if (g?.found) {
          end = { lat: g.lat, lng: g.lng, address: g.formatted_address || endAddress };
        } else {
          toast.error(`Non riesco a trovare "${endAddress}". Prova un indirizzo più preciso.`);
          setTempSaving(false);
          return;
        }
      } else {
        toast.error("Inserisci una destinazione.");
        setTempSaving(false);
        return;
      }

      const { data: resp } = await api.post("/driver/route-search/save-temporary", {
        start_lat: start.lat,
        start_lng: start.lng,
        end_lat: end.lat,
        end_lng: end.lng,
        start_address: start.address,
        end_address: end.address,
        max_detour_km: data?.max_detour_km || finalDetour,
        duration_hours: 2,
      });
      setTempActive(true);
      setTempExpiresAt(resp?.expires_at || null);
      toast.success(`Avvisi attivi per 2h · ${start.address.split(",")[0]} → ${end.address.split(",")[0]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTempSaving(false);
    }
  }, [data, tempActive, finalDetour, startPos, endPlace, endAddress, manualMode, manualStart]);

  // Phase 43.31 — Il bottone "Attiva avvisi" deve essere abilitato anche senza
  // coordinate risolte: in quel caso geocodifichiamo al volo via /maps/geocode.
  // Basta avere TESTO in partenza e destinazione (o GPS + destinazione).
  const hasStartText = !!(gpsReady || (manualMode && manualStart.trim().length > 0));
  const hasEndText = !!(endPlace?.lat != null || endAddress.trim().length > 0);
  const canEnableNotify = hasStartText && hasEndText;

  // Phase 43.31 — All'apertura della pagina, controlla se c'è già una temp route attiva
  // → riflette lo stato nel bottone (Disattiva avvisi vs Attiva avvisi)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: routes } = await api.get("/driver/routes");
        if (cancelled) return;
        const temp = (routes || []).find((r) => r.kind === "temporary" && r.active);
        if (temp) {
          // verifica che non sia scaduta
          if (temp.expires_at) {
            const exp = new Date(temp.expires_at);
            if (exp.getTime() > Date.now()) {
              setTempActive(true);
              setTempExpiresAt(temp.expires_at);
            }
          } else {
            setTempActive(true);
          }
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-10"
      data-testid="driver-route-search"
    >
      {/* Phase 43.40 — Back link in alto per navigazione comoda */}
      <button
        onClick={() => navigate("/driver")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-blue hover:text-dapnow-blue-hover transition self-start"
        data-testid="back-to-driver-top"
      >
        ← Torna alla Dashboard
      </button>

      {/* Hero — molto visibile, da feature principale */}
      <section className="rounded-3xl overflow-hidden border border-dapnow-line bg-gradient-to-br from-dapnow-blue via-blue-700 to-indigo-900 text-white p-6 sm:p-8 relative">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.35) 0, transparent 50%)",
        }} />
        <div className="relative">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-semibold">Driver · Feature</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-1">
            Sulla mia strada
          </h1>
          <p className="text-sm sm:text-base text-white/80 mt-2 max-w-2xl leading-relaxed">
            Stai andando da qualche parte? Trova consegne compatibili lungo il tuo percorso.
          </p>
        </div>
      </section>

      {/* GPS state — banner BIG quando denied (CTA evidente) */}
      {gpsState === "requesting" && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50/60 px-4 py-3 text-sm text-blue-900 flex items-center gap-2" data-testid="gps-requesting">
          <Loader2 className="size-4 animate-spin" /> Cerco la tua posizione…
        </div>
      )}
      {gpsState === "granted" && startPos && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-900 flex items-center gap-2" data-testid="gps-granted-banner">
          <Crosshair className="size-4" />
          <span className="font-semibold">Partenza: posizione attuale rilevata</span>
          <button onClick={requestGps} className="ml-auto text-xs text-emerald-700/70 hover:text-emerald-900 flex items-center gap-1">
            <RefreshCw className="size-3" /> Aggiorna
          </button>
        </div>
      )}
      {(gpsState === "idle" || gpsState === "denied" || gpsState === "unavailable") && !manualMode && !startPos && (
        <div className="card-soft border border-amber-200 bg-amber-50/40 p-5 sm:p-6 space-y-3" data-testid="gps-denied-banner">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-amber-100 p-2.5 shrink-0">
              <Crosshair className="size-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg font-bold tracking-tight text-amber-900">
                Abilita la posizione per trovare consegne sulla tua strada
              </h3>
              <p className="text-sm text-amber-900/80 mt-1.5 leading-relaxed">
                DapNow usa la tua posizione attuale come partenza e ti mostra le consegne compatibili verso la tua destinazione.
              </p>
              {gpsState === "denied" && (
                <div className="mt-2 text-xs text-amber-900/80 leading-relaxed space-y-1.5">
                  <p><strong>Permesso negato?</strong></p>
                  {isIOS ? (
                    <p>
                      Su iPhone tocca <strong>AA / lucchetto</strong> nella barra indirizzi Safari →{" "}
                      <strong>Impostazioni sito web → Posizione → Consenti</strong>.
                      In alternativa: <strong>Impostazioni → Safari → Posizione</strong>.
                      Poi torna qui e tocca "Abilita posizione".
                    </p>
                  ) : (
                    <p>
                      Tocca il <strong>lucchetto/scudo</strong> nella barra del browser →{" "}
                      <strong>Posizione → Consenti</strong>, poi torna qui e tocca "Abilita posizione".
                    </p>
                  )}
                </div>
              )}
              {gpsState === "unavailable" && (
                <p className="text-xs text-amber-900/80 mt-2 leading-relaxed">
                  Il tuo dispositivo non sembra fornire una posizione attendibile. Usa la partenza manuale per continuare.
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={requestGps} data-testid="gps-enable-btn"
                        className="h-11 px-5 rounded-xl bg-amber-700 text-white text-sm font-semibold hover:bg-amber-800 flex items-center gap-2">
                  <Crosshair className="size-4" /> Abilita posizione
                </button>
                <button onClick={() => setManualMode(true)} data-testid="manual-start-toggle"
                        className="h-11 px-5 rounded-xl bg-white text-amber-900 text-sm font-semibold border border-amber-300 hover:bg-amber-100">
                  Inserisci partenza manualmente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form ricerca — hero card grande */}
      <form onSubmit={search} className="card-soft p-6 sm:p-7 space-y-5" data-testid="search-form">
        {/* Partenza manuale solo se fallback */}
        {manualMode && !startPos && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-dapnow-muted mb-1.5">
              Partenza manuale
            </label>
            <input
              type="text"
              value={manualStart}
              onChange={(e) => setManualStart(e.target.value)}
              placeholder="es. Lugano"
              data-testid="manual-start-input"
              className="w-full h-12 px-4 rounded-xl border border-dapnow-line bg-white text-base"
            />
          </div>
        )}

        {/* Destinazione — campo grande, hero, con autocomplete Google Places */}
        <div>
          <label className="block text-sm font-semibold text-dapnow-ink mb-2">
            Dove stai andando?
          </label>
          <div className="relative">
            <MapPin className="size-5 text-dapnow-blue absolute left-4 top-[1.05rem] z-10 pointer-events-none" />
            <AddressAutocompleteInput
              value={endAddress}
              onChange={handleEndChange}
              onPlaceSelected={handleEndPlaceSelected}
              placeholder="Es. Bellinzona, Locarno, Lugano…"
              dataTestid="end-input"
              required
              className="w-full h-14 pl-12 pr-4 rounded-2xl border-2 border-dapnow-line bg-white text-base placeholder:text-dapnow-muted/70 focus:border-dapnow-blue focus:outline-none transition"
            />
          </div>
          {endAddress && !endPlace && (
            <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5" data-testid="end-suggest-hint">
              <AlertCircle className="size-3.5" /> Seleziona una destinazione dai suggerimenti.
            </p>
          )}
          {endPlace && (
            <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1.5" data-testid="end-selected">
              <Check className="size-3.5" /> Destinazione selezionata
            </p>
          )}
        </div>

        {/* Max detour */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-dapnow-muted mb-2">
            Deviazione massima
          </label>
          <div className="flex flex-wrap gap-2">
            {PRESET_DETOURS.map((km) => (
              <button
                key={km}
                type="button"
                onClick={() => { setMaxDetourKm(km); setCustomDetour(""); }}
                data-testid={`detour-${km}`}
                className={`px-4 h-11 rounded-xl text-sm font-semibold border transition ${
                  customDetour === "" && maxDetourKm === km
                    ? "bg-dapnow-blue text-white border-dapnow-blue shadow-sm"
                    : "bg-white text-dapnow-ink border-dapnow-line hover:border-dapnow-blue/40"
                }`}
              >
                {km} km
              </button>
            ))}
            <div className="flex items-center gap-1.5 rounded-xl border border-dapnow-line bg-white px-2">
              <input
                type="number" min="0.5" max="50" step="0.5"
                value={customDetour}
                onChange={(e) => setCustomDetour(e.target.value)}
                placeholder="Personalizzata"
                data-testid="detour-custom"
                className="w-28 h-11 text-sm bg-transparent text-center"
              />
              <span className="text-xs text-dapnow-muted pr-2">km</span>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !canSearch}
          data-testid="search-submit"
          className="w-full h-14 rounded-2xl bg-dapnow-blue text-white font-display text-lg font-bold tracking-tight hover:bg-dapnow-blue-hover disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm"
        >
          {loading ? <Loader2 className="size-5 animate-spin" /> : <Search className="size-5" />}
          Trova consegne
        </button>
        {!canSearch && !loading && (
          <p className="text-xs text-dapnow-muted text-center">
            {gpsState === "requesting" ? "In attesa della posizione…" : "Abilita la posizione o inserisci una partenza manuale per iniziare."}
          </p>
        )}
      </form>

      {/* Phase 43.31 — Standalone CTA "Avvisami" — sempre visibile.
          Disabled finché start+end non sono entrambi completi.
          Il senso è ricevere push quando arrivano nuovi ordini compatibili,
          non solo quando ce ne sono già adesso. */}
      <div
        data-testid="notify-temporary-card"
        className={`rounded-2xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 transition ${
          tempActive
            ? "border-emerald-300 bg-emerald-50/70"
            : canEnableNotify
              ? "border-dapnow-line bg-white"
              : "border-dapnow-line bg-dapnow-bg/40"
        }`}
      >
        <div className={`size-10 rounded-xl flex items-center justify-center shrink-0 ${
          tempActive
            ? "bg-emerald-600 text-white"
            : canEnableNotify
              ? "bg-dapnow-blue/10 text-dapnow-blue"
              : "bg-dapnow-muted/10 text-dapnow-muted"
        }`}>
          {tempActive ? <BellRing className="size-5" /> : <Bell className="size-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold ${canEnableNotify || tempActive ? "text-dapnow-ink" : "text-dapnow-muted"}`}>
            {tempActive ? "Avvisi attivi · 2h" : "Avvisami se arrivano consegne sulla mia strada"}
          </p>
          <p className="text-xs text-dapnow-muted mt-0.5">
            {tempActive
              ? "Riceverai una push se entro 2h appare un nuovo ordine compatibile."
              : canEnableNotify
                ? "Push automatica appena un cliente paga un ordine compatibile col tuo percorso (anche se ora non ce ne sono)."
                : "Inserisci partenza e destinazione qui sopra per attivare gli avvisi."}
          </p>
        </div>
        <button
          type="button"
          onClick={onSaveTemporary}
          disabled={tempSaving || (!tempActive && !canEnableNotify)}
          data-testid="enable-temporary-notify-btn"
          className={`h-12 w-full sm:w-auto px-4 rounded-xl text-base sm:text-sm font-bold tracking-tight transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shrink-0 ${
            tempActive
              ? "bg-white text-emerald-800 border border-emerald-300 hover:bg-emerald-100"
              : "bg-dapnow-blue text-white hover:bg-dapnow-blue-hover"
          }`}
        >
          {tempSaving && <Loader2 className="size-4 animate-spin" />}
          {tempActive ? "Disattiva avvisi" : "Attiva avvisi"}
        </button>
      </div>

      {/* Results */}
      {data && (
        <section className="space-y-4" data-testid="results-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-dapnow-muted">
                <strong className="text-dapnow-ink">Posizione attuale</strong>
                <ArrowRight className="inline size-3.5 mx-1.5" />
                <strong className="text-dapnow-ink">{data.end?.address}</strong>
                <span className="ml-2 text-dapnow-muted">· {formatKm(data.base_distance_km)} · max {data.max_detour_km}km</span>
              </p>
              <p className="text-xs text-dapnow-muted mt-0.5">
                Deviazione calcolata da <strong>posizione attuale → destinazione</strong> ·{" "}
                <strong data-testid="results-count">{data.results_count}</strong> consegne compatibili
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.results_count > 0 && (
                <button
                  onClick={() => setSaveModalOpen(true)}
                  data-testid="save-route-btn"
                  className="btn-ghost"
                >
                  <Save className="size-4" /> Salva come tragitto abituale
                </button>
              )}
            </div>
          </div>

          {data.results_count === 0 && (
            <div className="card-soft p-8 text-center" data-testid="empty-results">
              <Sparkles className="size-8 text-dapnow-muted mx-auto mb-2" />
              <p className="text-sm text-dapnow-muted">
                Nessuna consegna sulla tua strada entro {finalDetour}km di deviazione. Prova con un raggio più ampio.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {(data.results || []).map((o) => (
              <article
                key={o.id}
                data-testid={`result-card-${o.id}`}
                className="card-soft p-4 sm:p-5 hover:shadow-md transition"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      data-testid={`compat-${o.id}`}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border ${compatColor(o.compatibility_score)}`}
                    >
                      <Sparkles className="size-3" /> {o.compatibility_score}% · {o.compatibility_label}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-dapnow-bg border border-dapnow-line px-2.5 py-1 text-xs text-dapnow-muted">
                      Deviazione stimata · {formatKm(o.estimated_detour_km)}
                    </span>
                    {o.delivery_type && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-dapnow-bg border border-dapnow-line px-2.5 py-1 text-xs text-dapnow-muted capitalize">
                        {o.delivery_type}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    {(() => {
                      // Phase 43.41 — payout robusto: prendi il primo valore > 0 tra i fallback;
                      // se TUTTI sono 0/missing, calcola dal prezzo cliente come ultima risorsa (80%).
                      const fallbacks = [
                        o.driver_payout,
                        o.driver_payout_amount,
                        o.driver_earning,
                      ];
                      let payout = fallbacks.find((v) => typeof v === "number" && v > 0);
                      if (payout == null) {
                        const cp = o.final_price ?? o.price ?? o.gross_price ?? 0;
                        payout = Math.round(cp * 0.8 * 100) / 100;
                      }
                      return (
                        <p
                          className="font-display text-xl font-extrabold tabular-nums text-emerald-700"
                          data-testid={`payout-${o.id}`}
                        >
                          {formatCHF(payout)}
                        </p>
                      );
                    })()}
                    <p className="text-[11px] text-dapnow-muted font-semibold uppercase tracking-wider">
                      Guadagno driver
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5 text-sm">
                  <div className="flex items-start gap-2">
                    <Package className="size-4 mt-0.5 text-dapnow-blue shrink-0" />
                    <span><strong>Ritiro:</strong> {o.pickup_address}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="size-4 mt-0.5 text-dapnow-red shrink-0" />
                    <span><strong>Consegna:</strong> {o.delivery_address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-dapnow-muted pt-1">
                    {o.size && <span>Dim · {SIZE_LABEL[o.size] || o.size}</span>}
                    {o.urgency && <span>Priorità · {URGENCY_LABEL[o.urgency] || o.urgency}</span>}
                    <span>Distanza dalla tua posizione · {formatKm(o.distance_from_start_km)}</span>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-3 border-t border-dapnow-line">
                  <button
                    onClick={() => {
                      // Phase 43.26.1 — passa contesto match via query string per header dettaglio
                      const qs = new URLSearchParams({
                        from: "route_search",
                        detour: String(o.estimated_detour_km),
                        score: String(o.compatibility_score),
                        end: data.end?.address || "",
                      });
                      navigate(`/driver/orders/${o.id}?${qs.toString()}`);
                    }}
                    data-testid={`view-${o.id}`}
                    className="btn-ghost flex-1 sm:flex-none"
                  >
                    <Eye className="size-4" /> Visualizza
                  </button>
                  <button
                    onClick={() => acceptOrder(o.id)}
                    disabled={acceptingId === o.id}
                    data-testid={`accept-${o.id}`}
                    className="h-10 px-4 rounded-xl bg-dapnow-blue text-white font-semibold text-sm hover:bg-dapnow-blue-hover disabled:opacity-60 flex-1 sm:flex-none flex items-center justify-center gap-2"
                  >
                    {acceptingId === o.id ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                    Accetta
                  </button>
                </div>
              </article>
            ))}
          </div>

          <p className="text-[11px] text-dapnow-muted text-center pt-2">
            Le deviazioni in lista sono stime geometriche. Apri "Visualizza" per il percorso dettagliato Google Maps.
          </p>
        </section>
      )}

      {/* Save-as-route modal */}
      {saveModalOpen && data && (
        <SaveRouteModal
          data={data}
          maxDetourKm={finalDetour}
          onClose={() => setSaveModalOpen(false)}
          onSaved={() => { setSaveModalOpen(false); toast.success("Tragitto salvato tra i tuoi percorsi abituali."); }}
        />
      )}
    </div>
  );
}


function SaveRouteModal({ data, maxDetourKm, onClose, onSaved }) {
  const defaultName = `${(data.start?.address || "Partenza").split(",")[0]} → ${(data.end?.address || "Destinazione").split(",")[0]}`;
  const [routeName, setRouteName] = useState(defaultName);
  const [days, setDays] = useState({ mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false });
  const [saving, setSaving] = useState(false);

  const toggleDay = (id) => setDays((d) => ({ ...d, [id]: !d[id] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/driver/routes", {
        route_name: routeName.trim() || defaultName,
        start_address: data.start?.address || "",
        start_lat: data.start?.lat,
        start_lng: data.start?.lng,
        end_address: data.end?.address || "",
        end_lat: data.end?.lat,
        end_lng: data.end?.lng,
        max_detour_km: maxDetourKm,
        usual_days: Object.keys(days).filter((d) => days[d]),
      });
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
      data-testid="save-route-modal"
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-dapnow-line">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-blue-50 p-2.5">
              <Navigation className="size-5 text-dapnow-blue" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Salva tragitto abituale</h2>
              <p className="text-xs text-dapnow-muted mt-1">Riceverai notifiche per ordini compatibili sui giorni selezionati.</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4 text-sm">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-dapnow-muted mb-1.5">Nome tragitto</label>
            <input
              type="text"
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
              data-testid="save-route-name"
              className="w-full h-11 px-3 rounded-xl border border-dapnow-line bg-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-dapnow-muted mb-2">Giorni della settimana</label>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAY_OPTIONS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggleDay(d.id)}
                  data-testid={`day-${d.id}`}
                  className={`w-12 h-10 rounded-lg text-xs font-semibold border transition ${
                    days[d.id]
                      ? "bg-dapnow-blue text-white border-dapnow-blue"
                      : "bg-white text-dapnow-muted border-dapnow-line"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="text-xs text-dapnow-muted">
            Deviazione massima: <strong className="text-dapnow-ink">{maxDetourKm} km</strong>
          </div>
        </div>
        <div className="px-6 py-4 bg-dapnow-bg border-t border-dapnow-line flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button onClick={onClose} disabled={saving} className="btn-ghost h-11 px-5">
            Annulla
          </button>
          <button
            onClick={save}
            disabled={saving || !routeName.trim()}
            data-testid="save-route-submit"
            className="h-11 px-5 rounded-xl bg-dapnow-blue text-white font-semibold tracking-tight hover:bg-dapnow-blue-hover disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Salva tragitto
          </button>
        </div>
      </div>
    </div>
  );
}
